import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { AuthProvider } from './types';
import { createConfigurationError } from './utils/errorHandling';

/**
 * Centralized authentication configuration utility
 *
 * This module provides a unified way to check authentication settings
 * and determine whether authentication should be enabled or disabled.
 * It maintains backward compatibility with the existing VITE_DISABLE_KEYCLOAK_AUTH
 * flag while providing a more generic approach for both authentication providers.
 */

/**
 * Check if authentication is globally disabled
 *
 * This function checks ONLY the global authentication disable flag.
 * Provider-specific disable flags (like VITE_DISABLE_KEYCLOAK_AUTH) are handled
 * separately and do not affect other authentication providers.
 *
 * @returns True if ALL authentication should be disabled globally
 */
export function isAuthDisabled(): boolean {
  const config = getRuntimeConfig();

  // Only check the global auth disable flag
  return config.VITE_DISABLE_AUTH === 'true';
}

/**
 * Check if Keycloak authentication is specifically disabled
 * This is separate from global auth disabling and only affects Keycloak provider
 *
 * @returns True if Keycloak authentication should be disabled
 */
export function isKeycloakAuthDisabled(): boolean {
  const config = getRuntimeConfig();
  return config.VITE_DISABLE_KEYCLOAK_AUTH === 'true';
}

/**
 * Valid authentication provider values
 */
const VALID_AUTH_PROVIDERS = new Set<AuthProvider>([
  'keycloak',
  'entra',
  'magic-link',
]);

/**
 * Type guard to check if a value is a valid AuthProvider
 */
function isValidAuthProvider(value: string): value is AuthProvider {
  return ['keycloak', 'entra', 'magic-link'].includes(value);
}

/**
 * Safely convert unknown value to string for error messages
 */
function safeStringify(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[object Object]';
    }
  }
  // Handle functions, symbols, and other edge cases
  return typeof value;
}

/**
 * Normalize configuration value to string with enhanced type safety
 */
function normalizeConfigValue(value: unknown): string {
  if (typeof value === 'string') return value.toLowerCase().trim();
  if (typeof value === 'number') return value.toString().toLowerCase();
  if (value === null || value === undefined) return '';
  return 'invalid';
}

/**
 * Get the configured authentication provider
 *
 * This function implements fail-fast behavior for invalid or missing AUTH_PROVIDER values.
 * No fallbacks are provided to ensure explicit configuration.
 *
 * @returns The configured authentication provider
 * @throws Error if AUTH_PROVIDER is invalid or missing
 */
export function getAuthProvider(): AuthProvider {
  const config = getRuntimeConfig();
  const authProvider = config.AUTH_PROVIDER;

  // Require explicit AUTH_PROVIDER configuration
  if (!authProvider) {
    throw createConfigurationError(
      'AUTH_PROVIDER environment variable is required. Set to "keycloak", "entra", or "magic-link".',
      'getAuthProvider',
    );
  }

  // Normalize and validate provider value with enhanced type safety
  const normalizedProvider = normalizeConfigValue(authProvider);

  if (isValidAuthProvider(normalizedProvider)) {
    return normalizedProvider;
  }

  // Fail fast for invalid provider values with better error context
  const validProviders = Array.from(VALID_AUTH_PROVIDERS).join(', ');
  const authProviderStr = safeStringify(authProvider);
  throw createConfigurationError(
    `Invalid AUTH_PROVIDER value: "${authProviderStr}" (normalized: "${normalizedProvider}"). Must be one of: ${validProviders}.`,
    'getAuthProvider',
  );
}
